import React, { useMemo, useState } from 'react';
import './css/index.scss';
import MyButton from '../../../../components/MyButton';
import PasswordServices from '../../../../helpers/PasswordServices';
import SearchInput from '../../../../components/SearchInput';
import MyModal from '../../../../components/MyModal';
import useInputState from '../../../../helpers/hooks/useInputState';

const FIENTA_CODE = () => `
async function collectAndSendFientaHtml() {
  console.log('Начинаем сбор HTML со страницы Fienta...');

  try {
    // Проверяем, что мы на правильной странице
    const currentUrl = window.location.href;
    if (!currentUrl.includes('fienta.com')) {
      console.error('❌ Ошибка: Вы должны быть на странице fienta.com');
      return;
    }

    console.log('📍 Текущий URL:', currentUrl);

    // Ждем загрузки страницы
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Функция для нажатия на кнопку "Load more"
    const clickLoadMore = async () => {
      const loadMoreBtn = document.querySelector('button#load-more-btn');
      if (!loadMoreBtn) {
        return false;
      }
      
      // Проверяем видимость кнопки
      const isVisible = loadMoreBtn.offsetParent !== null && 
                       !loadMoreBtn.disabled && 
                       loadMoreBtn.style.display !== 'none';
      
      if (isVisible) {
        console.log('🔄 Нажимаем на кнопку "Load more"...');
        loadMoreBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
        await new Promise(resolve => setTimeout(resolve, 500)); // Небольшая задержка перед кликом
        loadMoreBtn.click();
        await new Promise(resolve => setTimeout(resolve, 2000)); // Ждем загрузки
        return true;
      }
      return false;
    };

    // Нажимаем на кнопку пока она есть
    let clickCount = 0;
    let hasMore = true;
    while (hasMore) {
      hasMore = await clickLoadMore();
      if (hasMore) {
        clickCount++;
        console.log(\`✅ Нажатие #\${clickCount} выполнено, ждем загрузки...\`);
        await new Promise(resolve => setTimeout(resolve, 3000)); // Дополнительное ожидание
      }
    }

    console.log(\`✅ Всего нажатий на "Load more": \${clickCount}\`);
    console.log('⏳ Ждем финальной загрузки...');
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Получаем div#events
    const eventsDiv = document.querySelector('div#events');
    if (!eventsDiv) {
      console.error('❌ Ошибка: div#events не найден на странице!');
      return;
    }

    console.log('✅ div#events найден');
    
    // Подсчитываем количество мероприятий
    const eventCards = eventsDiv.querySelectorAll('article.event-card');
    const eventsCount = eventCards.length;
    console.log(\`📊 Найдено мероприятий: \${eventsCount}\`);
    
    // Парсим события в JSON
    const parseEvents = () => {
      const events = [];
      eventCards.forEach((card) => {
        try {
          const linkEl = card.querySelector('a[href*="fienta.com"]');
          const href = linkEl ? (linkEl.getAttribute('href') || '').split('#')[0].trim() : '';
          
          const titleEl = card.querySelector('.event-card-title h2');
          const title = titleEl ? titleEl.textContent.trim() : '';
          
          const smallPs = card.querySelectorAll('.event-card-body p.small');
          const dateText = smallPs[0] ? smallPs[0].textContent.trim() : '';
          const venueText = smallPs[1] ? smallPs[1].textContent.trim() : '';
          
          if (href && title) {
            events.push({
              href,
              title,
              date: dateText,
              venue: venueText
            });
          }
        } catch (err) {
          console.error('Ошибка при парсинге карточки:', err);
        }
      });
      return events;
    };
    
    const eventsData = parseEvents();
    console.log(\`✅ Извлечено \${eventsData.length} событий\`);
    
    // Создаем JSON
    const jsonData = JSON.stringify(eventsData, null, 2);
    const jsonSize = (jsonData.length / 1024).toFixed(2);
    console.log(\`📦 Размер JSON: \${jsonSize} KB\`);
    
    // Получаем HTML содержимое (для обратной совместимости)
    const htmlContent = eventsDiv.outerHTML;
    const htmlSize = (htmlContent.length / 1024).toFixed(2);
    console.log(\`📦 Размер HTML: \${htmlSize} KB\`);

    // Функция для копирования в буфер обмена
    const copyToClipboard = async (text) => {
      try {
        // Сначала пробуем современный API (если доступен и документ в фокусе)
        if (navigator.clipboard && navigator.clipboard.writeText && document.hasFocus()) {
          try {
            await navigator.clipboard.writeText(text);
            return true;
          } catch (err) {
            // Если не получилось, используем fallback
            console.log('Clipboard API failed, using fallback method');
          }
        }
        
        // Надежный fallback метод с textarea (работает всегда)
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-999999px';
        textarea.style.top = '-999999px';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        
        let success = false;
        try {
          success = document.execCommand('copy');
        } catch (err) {
          console.error('execCommand failed:', err);
        }
        
        document.body.removeChild(textarea);
        return success;
      } catch (err) {
        console.error('Ошибка при копировании:', err);
        return false;
      }
    };

    // Сохраняем данные в глобальные переменные
    window.collectedFientaHtml = htmlContent;
    window.collectedFientaJson = jsonData;
    
    // Показываем alert с предложением скопировать
    const alertMessage = \`📊 Найдено \${eventsCount} мероприятий.\\n\\nJSON: \${jsonSize} KB (оптимизировано)\\nHTML: \${htmlSize} KB\\n\\nНажмите OK, чтобы скопировать JSON в буфер обмена.\`;
    
    const userConfirmed = confirm(alertMessage);
    
    if (userConfirmed) {
      // Возвращаем фокус на window перед копированием
      window.focus();
      // Небольшая задержка для восстановления фокуса
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const copied = await copyToClipboard(jsonData);
      
      if (copied) {
        alert('✅ JSON успешно скопирован в буфер обмена!');
        console.log('✅ JSON скопирован в буфер обмена');
        console.log('💾 HTML также доступен в window.collectedFientaHtml');
      } else {
        alert('❌ Не удалось скопировать в буфер обмена. JSON доступен в window.collectedFientaJson');
        console.error('❌ Не удалось скопировать в буфер обмена');
      }
    }

  } catch (error) {
    console.error('❌ Критическая ошибка:', error);
    if (error.message) {
      console.error('Сообщение:', error.message);
    }
    if (error.stack) {
      console.error('Stack:', error.stack);
    }
  }
}

collectAndSendFientaHtml();
`;





function FientaInstructions() {
  const [copied, setCopied] = useState(false);
  const [htmlContent, setHtmlContent, htmlError, setHtmlError] = useInputState('');
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalState, setModalState] = useState('');

  const hash = useMemo(() => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const dateStr = `${day}.${month}.${year}`;
    const hashString = `ieowhfew-${dateStr}-dneoiwd`;
    return PasswordServices.encryptPassword(hashString);
  }, []);

  const handleCopyCode = async () => {
    try {
      const code = FIENTA_CODE();
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Не удалось скопировать код. Попробуйте выделить текст вручную.');
    }
  };

  const validateHtml = () => {
    if (!htmlContent || htmlContent.trim().length < 10) {
      setHtmlError('HTML контент обязателен и должен быть не менее 10 символов');
      return false;
    }
    setHtmlError('');
    return true;
  };

  const handleSubmitHtml = async () => {
    if (!validateHtml()) {
      return;
    }

    setLoading(true);
    setIsModalVisible(false);

    try {
      const parsingServerUrl = process.env.REACT_APP_PARSING_SERVER_URL;
      const parsingServerApiKey = process.env.REACT_APP_PARSING_SERVER_API_KEY;

      const response = await fetch(`${parsingServerUrl}/parsing/submit-fienta-html`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': parsingServerApiKey,
        },
        body: JSON.stringify({
          html: htmlContent.trim(),
        }),
      });

      const result = await response.json();

      setIsModalVisible(true);
      if (response.ok) {
        const htmlPageId = result.htmlPageId || 'не указан';
        setModalText(`Данные успешно отправлены на сервер!\n\nHTML Page ID: ${htmlPageId}`);
        setModalState('success');
        setHtmlContent('', true);
      } else {
        const errorText = result.message || 'Не удалось отправить HTML на сервер.';
        setModalText(`Возникла ошибка:\n\n${errorText}`);
        setModalState('error');
      }
    } catch (error) {
      setIsModalVisible(true);
      let errorMessage = 'Не удалось установить соединение с сервером.';
      
      if (error.message.includes('SSL') || error.message.includes('Failed to fetch')) {
        errorMessage = 'Ошибка: Браузер блокирует HTTP запрос с HTTPS страницы (Mixed Content).';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setModalText(`Возникла ошибка:\n\n${errorMessage}`);
      setModalState('error');
    } finally {
      setLoading(false);
    }
  };

  const onCloseModal = () => {
    setIsModalVisible(false);
    setTimeout(() => {
      setModalText('');
      setModalState('');
    }, 400);
  };

  return (
    <div className="fienta_instructions">
      <div className="fienta_instructions_left">
        <div className="fienta_instructions_text">
          <p>
            1. Перейдите на страницу Fienta именно через{' '}
            <a
              href="https://fienta.com/?p=63"
              target="_blank"
              rel="noopener noreferrer"
              className="fienta_instructions_link"
            >
              эту ссылку
            </a>
            .
          </p>
          <p>
            2. Откройте консоль браузера:
            <br />
            • <strong>Chrome/Edge:</strong> Правая кнопка мыши → «Исследовать» (Inspect) → вкладка «Console»
            <br />
            • <strong>Firefox:</strong> Правая кнопка мыши → «Исследовать элемент» (Inspect Element) → вкладка «Console»
            <br />
            • <strong>Safari:</strong> Правая кнопка мыши → «Проверить элемент» (Inspect Element) → вкладка «Console»
          </p>
          <p>
            3. Вставьте и вызовите (вызвать = ENTER) код, указанный ниже.
          </p>
          <p>
            4. После успешного выполнения скопируйте полученный текст из всплывшего окна.
          </p>
          <p>
            5. Вернитесь сюда, вставьте скопированный текст в правое поле и нажмите «Отправить».
          </p>
          <p>
            Если всё пройдёт успешно, то в ближайшее воскресенье данные уже обработаются в нашей системе.
          </p>
        </div>

        <div className="fienta_instructions_code_block">
          <textarea
            className="fienta_instructions_textarea"
            value={FIENTA_CODE()}
            readOnly
          />
          <MyButton
            text={copied ? '✓ Скопировано!' : 'Копировать код'}
            onClick={handleCopyCode}
            className="fienta_instructions_copy_button"
            secondary={copied}
          />
        </div>
      </div>

      <div className="fienta_instructions_right">
        <div className="fienta_instructions_submit_block">
          <h3 className="fienta_instructions_submit_title">Отправка HTML</h3>
          <p className="fienta_instructions_submit_description">
            Вставьте контент, собранный со страницы Fienta, в поле ниже и нажмите кнопку для отправки на сервер.
          </p>
          <SearchInput
            wrapperClassName="fienta_instructions_html_input"
            placeholder="Вставьте контент здесь..."
            withLeftIcon={false}
            value={htmlContent}
            onChange={setHtmlContent}
            error={htmlError}
            isLarge
            shouldValueConsiderAsFocus={false}
          />
          <MyButton
            text="Отправить"
            onClick={handleSubmitHtml}
            loading={loading}
            className="fienta_instructions_submit_button"
          />
        </div>
      </div>

      <MyModal isOpen={isModalVisible} closeFunction={onCloseModal}>
        <div style={{ padding: '20px' }}>
          <p style={{ whiteSpace: 'pre-wrap', marginBottom: '20px' }}>{modalText}</p>
          <MyButton
            text={modalState === 'error' ? 'Закрыть' : 'ОК'}
            onClick={onCloseModal}
          />
        </div>
      </MyModal>
    </div>
  );
}

export default FientaInstructions;
